import { makeAutoObservable, reaction, runInAction } from "mobx";
import { ApiError, type ApiClient } from "@/clients/ApiClient";

/** Where a signed-out listener's progress is kept, on this device only */
const STORAGE_KEY = "hp_listening_progress";
const MAX_LOCAL_ENTRIES = 100;
const MAX_LOCAL_AGE_MS = 180 * 24 * 60 * 60 * 1000;
/** The most episodes the API accepts in one `?ids=` request */
const MAX_IDS = 100;
/** Progress is sent at most this often while listening; pausing, finishing and closing the page send at once */
const SAVE_INTERVAL_MS = 15_000;
/** Lists register the episodes they show; these are fetched together after this short wait */
const WATCH_DELAY_MS = 50;
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 60_000;
/** Positions shorter than this are not worth resuming */
const MIN_RESUME_SECONDS = 10;
/** An episode counts as played when this much of it is done... */
const PLAYED_FRACTION = 0.95;
/** ...or when only this many seconds remain */
const PLAYED_REMAINING_SECONDS = 30;

type EpisodeSnapshot = ListeningEntryResponse["episode"];
type PodcastSnapshot = ListeningEntryResponse["podcast"];

export interface ListeningEntry {
    /** Seconds into the episode */
    position: number;
    /** Seconds; 0 when unknown */
    duration: number;
    played: boolean;
    /** Milliseconds since the epoch */
    updatedAt: number;
    episode: EpisodeSnapshot;
    podcast: PodcastSnapshot;
}

type Operation = { kind: "save"; position: number; duration: number; played?: boolean } | { kind: "forget" };

type ListeningApi = Pick<ApiClient, "get" | "put" | "delete">;

const isEntry = (value: unknown): value is ListeningEntry => {
    const entry = value as ListeningEntry | null;
    return (
        !!entry &&
        typeof entry.position === "number" &&
        typeof entry.duration === "number" &&
        typeof entry.updatedAt === "number" &&
        !!entry.episode &&
        !!entry.podcast
    );
};

const fromServer = (entry: ListeningEntryResponse): ListeningEntry => ({
    position: entry.position,
    duration: entry.duration,
    played: entry.played,
    updatedAt: Date.parse(entry.updated_at) || Date.now(),
    episode: entry.episode,
    podcast: entry.podcast,
});

const isDone = (position: number, duration: number) =>
    duration > 0 && (position / duration >= PLAYED_FRACTION || duration - position <= PLAYED_REMAINING_SECONDS);

/** Server errors that retrying cannot fix: the request itself or the session is wrong */
const isFinalFailure = (error: unknown) => error instanceof ApiError && error.status >= 400 && error.status < 500;

/** What we know about who is listening: decides where progress is kept */
interface ListeningAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
    readonly isSignedIn: boolean;
}

/**
 * Where the listener is in every episode they have started. Powers resuming, the progress bars
 * and the "Fortsätt lyssna" shelf.
 *
 * Signed in, the truth lives on the account (`/me/listening`), so it follows the listener between
 * devices; this keeps a copy in memory. Saving never blocks playback: changes are queued, sent at
 * most every 15 seconds (at once when pausing, finishing or leaving the page) and retried with a
 * growing delay if the API is down.
 *
 * Signed out, everything works the same but is kept on this device only, so being signed out is
 * never a worse experience. The device copy is left as it is on sign-in, for importing into the account.
 */
export class ListeningStore {
    entries: Record<string, ListeningEntry> = {};
    /** True once the unfinished episodes of the signed-in user have been fetched */
    loaded = false;

    private queue = new Map<number, Operation>();
    /** Episodes whose progress is already loaded or has been asked for */
    private known = new Set<number>();
    private wanted = new Set<number>();
    private generation = 0;
    private lastSent = 0;
    private failures = 0;
    private flushTimer: ReturnType<typeof setTimeout> | undefined;
    private retryTimer: ReturnType<typeof setTimeout> | undefined;
    private watchTimer: ReturnType<typeof setTimeout> | undefined;
    private disposers: Array<() => void> = [];

    constructor(
        private readonly client: ListeningApi,
        private readonly auth: ListeningAuth
    ) {
        makeAutoObservable<
            ListeningStore,
            | "client"
            | "auth"
            | "queue"
            | "known"
            | "wanted"
            | "generation"
            | "lastSent"
            | "failures"
            | "flushTimer"
            | "retryTimer"
            | "watchTimer"
            | "disposers"
        >(this, {
            client: false,
            auth: false,
            queue: false,
            known: false,
            wanted: false,
            generation: false,
            lastSent: false,
            failures: false,
            flushTimer: false,
            retryTimer: false,
            watchTimer: false,
            disposers: false,
        });

        this.disposers.push(
            reaction(
                () => this.auth.status,
                (status) => {
                    this.reset();
                    if (status === "authenticated") {
                        void this.load();
                    } else if (status === "anonymous") {
                        this.loadLocal();
                    }
                },
                { fireImmediately: true }
            )
        );

        if (typeof window !== "undefined") {
            // Leaving the page is the last chance to save; `keepalive` lets the requests finish
            const leave = () => void this.flush({ keepalive: true });
            const hidden = () => document.visibilityState === "hidden" && leave();
            window.addEventListener("pagehide", leave);
            document.addEventListener("visibilitychange", hidden);
            this.disposers.push(
                () => window.removeEventListener("pagehide", leave),
                () => document.removeEventListener("visibilitychange", hidden)
            );
        }
    }

    /** Progress is kept on the account for signed-in listeners, and on this device otherwise */
    get signedIn(): boolean {
        return this.auth.isSignedIn;
    }

    private get onDevice(): boolean {
        return this.auth.status === "anonymous";
    }

    /** Not before we know whether anyone is signed in */
    private get remembers(): boolean {
        return this.auth.status !== "unknown";
    }

    get inProgress(): ListeningEntry[] {
        return Object.values(this.entries)
            .filter((entry) => !entry.played && entry.position >= MIN_RESUME_SECONDS)
            .sort((a, b) => b.updatedAt - a.updatedAt);
    }

    /** The episode to bring back in the player after a reload: the one listened to last and not finished */
    get resumeCandidate(): ListeningEntry | undefined {
        return this.inProgress[0];
    }

    /** 0..1 for an episode that has been started and not finished, otherwise 0 */
    progress(episodeId: number): number {
        const entry = this.entries[episodeId];
        if (!entry || entry.played || entry.duration <= 0) return 0;
        return Math.min(1, entry.position / entry.duration);
    }

    entry(episodeId: number): ListeningEntry | undefined {
        return this.entries[episodeId];
    }

    isPlayed(episodeId: number): boolean {
        return this.entries[episodeId]?.played === true;
    }

    /** Where to start an episode: 0 for new or finished ones */
    resumePosition(episodeId: number): number {
        const entry = this.entries[episodeId];
        if (!entry || entry.played || entry.position < MIN_RESUME_SECONDS) return 0;
        return Math.floor(entry.position);
    }

    /** Read what this device remembers for a signed-out listener */
    loadLocal() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed: unknown = JSON.parse(raw);
                if (parsed && typeof parsed === "object") {
                    const cutoff = Date.now() - MAX_LOCAL_AGE_MS;
                    for (const [id, value] of Object.entries(parsed)) {
                        if (isEntry(value) && value.updatedAt >= cutoff) {
                            this.entries[id] = value;
                        }
                    }
                }
            }
        } catch {
            // Corrupt or blocked storage: start empty
        }
        this.loaded = true;
    }

    /** Fetch the unfinished episodes of the signed-in user. Called on sign-in. */
    async load() {
        if (!this.auth.isSignedIn) return;

        const generation = this.generation;
        try {
            const response = await this.client.get<ListeningResponse>("/me/listening?status=in_progress&limit=50");
            runInAction(() => {
                // Signed out, or in as someone else, while the request was on its way
                if (generation !== this.generation) return;
                this.merge(response.entries);
                this.loaded = true;
            });
        } catch (error) {
            // Not fatal: listening works, it just does not resume until the next visit
            console.warn("Could not load listening progress", error);
        }
    }

    /**
     * Ask for the progress of the episodes a list is showing, so their bars and "Spelad" labels appear.
     * Cheap to call repeatedly: episodes already loaded or asked for are skipped, the rest go in one request.
     */
    watch(episodeIds: number[]) {
        // On the device every entry is already in memory
        if (!this.auth.isSignedIn) return;

        for (const id of episodeIds) {
            if (id > 0 && !this.known.has(id)) {
                this.wanted.add(id);
            }
        }
        if (this.wanted.size > 0 && this.watchTimer === undefined) {
            this.watchTimer = setTimeout(() => void this.fetchWanted(), WATCH_DELAY_MS);
        }
    }

    record(episode: Episode, podcast: Podcast, position: number, duration: number) {
        if (!this.remembers || !Number.isFinite(position) || position < 0 || episode.id < 0) return;

        const knownDuration =
            Number.isFinite(duration) && duration > 0 ? duration : this.entries[episode.id]?.duration || 0;
        // The server rejects a position past the end; a stream can briefly report one
        const clamped = knownDuration > 0 ? Math.min(position, knownDuration) : position;
        this.entries[episode.id] = {
            position: clamped,
            duration: knownDuration,
            played: isDone(clamped, knownDuration),
            updatedAt: Date.now(),
            episode: this.snapshotEpisode(episode),
            podcast: this.snapshotPodcast(podcast),
        };
        if (this.onDevice) {
            this.saveLocal();
            return;
        }
        this.known.add(episode.id);
        this.queue.set(episode.id, { kind: "save", position: clamped, duration: knownDuration });
        this.scheduleFlush();
    }

    markPlayed(episode: Episode, podcast: Podcast) {
        if (!this.remembers || episode.id < 0) return;

        const existing = this.entries[episode.id];
        const duration = existing?.duration || 0;
        const position = duration || existing?.position || 0;
        this.entries[episode.id] = {
            position,
            duration,
            played: true,
            updatedAt: Date.now(),
            episode: this.snapshotEpisode(episode),
            podcast: this.snapshotPodcast(podcast),
        };
        if (this.onDevice) {
            this.saveLocal();
            return;
        }
        this.known.add(episode.id);
        this.queue.set(episode.id, { kind: "save", position, duration, played: true });
        void this.flush();
    }

    markUnplayed(episodeId: number) {
        if (!this.remembers) return;

        delete this.entries[episodeId];
        if (this.onDevice) {
            this.saveLocal();
            return;
        }
        this.known.add(episodeId);
        this.queue.set(episodeId, { kind: "forget" });
        void this.flush();
    }

    /**
     * Send what is queued. Failures are queued again (unless the API says the request itself is wrong)
     * and retried later, so callers never need to wait for or handle the result.
     */
    async flush(options: { keepalive?: boolean } = {}) {
        clearTimeout(this.flushTimer);
        this.flushTimer = undefined;
        if (!this.auth.isSignedIn || this.queue.size === 0) return;

        const batch = [...this.queue];
        this.queue.clear();
        this.lastSent = Date.now();

        const results = await Promise.allSettled(
            batch.map(([episodeId, operation]) => this.send(episodeId, operation, options.keepalive === true))
        );

        let retry = false;
        results.forEach((result, index) => {
            if (result.status === "fulfilled") return;

            const [episodeId, operation] = batch[index];
            if (isFinalFailure(result.reason)) {
                console.warn(`Dropped listening update for episode ${episodeId}`, result.reason);
            } else if (!this.queue.has(episodeId)) {
                // A newer change to the same episode replaces the failed one
                this.queue.set(episodeId, operation);
                retry = true;
            }
        });

        if (retry) {
            this.failures += 1;
            this.scheduleRetry();
        } else if (results.every((result) => result.status === "fulfilled")) {
            this.failures = 0;
        }
    }

    /** Forget everything held for the signed-in user, e.g. on sign-out */
    reset() {
        this.generation += 1;
        this.entries = {};
        this.loaded = false;
        this.queue.clear();
        this.known.clear();
        this.wanted.clear();
        this.failures = 0;
        this.lastSent = 0;
        clearTimeout(this.flushTimer);
        clearTimeout(this.retryTimer);
        clearTimeout(this.watchTimer);
        this.flushTimer = this.retryTimer = this.watchTimer = undefined;
    }

    dispose() {
        this.reset();
        this.disposers.forEach((dispose) => dispose());
        this.disposers = [];
    }

    private saveLocal() {
        const newest = Object.entries(this.entries)
            .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
            .slice(0, MAX_LOCAL_ENTRIES);
        this.entries = Object.fromEntries(newest);

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
        } catch {
            // Blocked or full: keep working in memory
        }
    }

    private send(episodeId: number, operation: Operation, keepalive: boolean) {
        const url = `/me/listening/${episodeId}`;
        if (operation.kind === "forget") {
            return this.client.delete(url, { keepalive });
        }

        const { position, duration, played } = operation;
        return this.client.put(url, played === undefined ? { position, duration } : { position, duration, played }, {
            keepalive,
        });
    }

    private scheduleFlush() {
        if (this.flushTimer !== undefined || this.retryTimer !== undefined) return;

        const wait = Math.max(0, this.lastSent + SAVE_INTERVAL_MS - Date.now());
        this.flushTimer = setTimeout(() => void this.flush(), wait);
    }

    private scheduleRetry() {
        clearTimeout(this.retryTimer);
        const wait = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (this.failures - 1));
        this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            void this.flush();
        }, wait);
    }

    private async fetchWanted() {
        this.watchTimer = undefined;
        const ids = [...this.wanted];
        this.wanted.clear();
        ids.forEach((id) => this.known.add(id));

        const generation = this.generation;
        for (let start = 0; start < ids.length; start += MAX_IDS) {
            const chunk = ids.slice(start, start + MAX_IDS);
            try {
                const response = await this.client.get<ListeningResponse>(`/me/listening?ids=${chunk.join(",")}`);
                runInAction(() => {
                    if (generation === this.generation) this.merge(response.entries);
                });
            } catch {
                // Let a later list ask again
                chunk.forEach((id) => this.known.delete(id));
            }
        }
    }

    /** Server entries replace what we hold, except episodes with a change still waiting to be sent */
    private merge(entries: ListeningEntryResponse[]) {
        for (const entry of entries) {
            this.known.add(entry.episode_id);
            if (!this.queue.has(entry.episode_id)) {
                this.entries[entry.episode_id] = fromServer(entry);
            }
        }
    }

    private snapshotEpisode(episode: Episode): EpisodeSnapshot {
        const { id, guid, title, slug, sound, published, length, duration, podcast_id, art } = episode;
        return { id, guid, title, slug, sound, published, length, duration, podcast_id, art };
    }

    private snapshotPodcast(podcast: Podcast): PodcastSnapshot {
        const { id, name, slug, art, blurhash } = podcast;
        return { id, name, slug, art, blurhash };
    }
}
